// Prezync media storage (Cloudflare R2 behind workers/media). Decks store media
// URLs without a pass; mediaSrc() adds the current pass when rendering.
// See docs/r2-storage-plan.md. With VITE_MEDIA_URL unset, uploads still go to
// the user's Google Drive.
import { auth } from './firebase.js';
import { uploadImageToDrive, uploadAudioToDrive, friendlyDriveError } from './drive.js';

const MEDIA_BASE = (import.meta.env.VITE_MEDIA_URL || '').replace(/\/+$/, '');
export const mediaEnabled = Boolean(MEDIA_BASE);

const MAX_SIDE = 2000;
const KEEP_AS_IS = new Set(['image/gif', 'image/svg+xml']);
const REFRESH_BEFORE_MS = 10 * 60 * 1000;
const PASS_STORE = 'pz_media_pass';

let ownerPass = null; // { uid, pass, expiresAt }
let ownerPassPromise = null;
let refreshTimer = null;

export function isMediaUrl(url) {
  return mediaEnabled && typeof url === 'string' && url.startsWith(`${MEDIA_BASE}/u/`);
}

// The URL to put in src/url(): stored media URLs get the current pass; every
// other URL is returned unchanged.
export function mediaSrc(url) {
  if (!isMediaUrl(url) || !ownerPass) return url;
  return `${url.split('?')[0]}?t=${encodeURIComponent(ownerPass.pass)}`;
}

function loadStoredPass(uid) {
  try {
    const p = JSON.parse(localStorage.getItem(PASS_STORE) || 'null');
    return p?.uid === uid && p.expiresAt - Date.now() > REFRESH_BEFORE_MS ? p : null;
  } catch {
    return null;
  }
}

function storePass(p) {
  try { localStorage.setItem(PASS_STORE, JSON.stringify(p)); } catch {}
}

function scheduleRefresh() {
  clearTimeout(refreshTimer);
  const wait = Math.max(30_000, ownerPass.expiresAt - Date.now() - REFRESH_BEFORE_MS);
  refreshTimer = setTimeout(() => { ownerPass = null; ensureOwnerPass().catch(() => {}); }, wait);
}

// Fetches (or reuses) the signed-in lecturer's pass. Pages that render the
// owner's media await this before their first paint: CSS backgrounds give no
// error event to retry on.
export async function ensureOwnerPass() {
  const user = auth?.currentUser;
  if (!mediaEnabled || !user || user.isAnonymous) return null;
  if (ownerPass?.uid === user.uid && ownerPass.expiresAt - Date.now() > REFRESH_BEFORE_MS) return ownerPass;
  const stored = loadStoredPass(user.uid);
  if (stored) {
    ownerPass = stored;
    scheduleRefresh();
    return ownerPass;
  }
  ownerPassPromise ||= (async () => {
    try {
      const res = await fetch(`${MEDIA_BASE}/pass/owner`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await user.getIdToken()}` },
      });
      if (!res.ok) throw await mediaError(res);
      const { pass, expiresAt } = await res.json();
      ownerPass = { uid: user.uid, pass, expiresAt };
      storePass(ownerPass);
      scheduleRefresh();
      return ownerPass;
    } finally {
      ownerPassPromise = null;
    }
  })();
  return ownerPassPromise;
}

export function clearMediaPass() {
  ownerPass = null;
  clearTimeout(refreshTimer);
  try { localStorage.removeItem(PASS_STORE); } catch {}
}

async function mediaError(res) {
  const body = await res.json().catch(() => ({}));
  const err = new Error(body.error || `Media request failed (${res.status})`);
  err.status = res.status;
  err.code = body.error;
  Object.assign(err, body);
  return err;
}

function canvasBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

// Scales to MAX_SIDE and re-encodes as WebP when that's smaller. GIF and SVG
// are left alone (animation, vectors).
export async function compressImage(file, { quality = 0.82 } = {}) {
  if (KEEP_AS_IS.has(file.type)) return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  let out = await canvasBlob(canvas, 'image/webp', quality);
  // Older Safari can't encode WebP and silently returns PNG.
  if (out?.type !== 'image/webp') out = file.type === 'image/jpeg' ? await canvasBlob(canvas, 'image/jpeg', quality) : null;
  if (!out) return file;
  return scale < 1 || out.size < file.size ? out : file;
}

function putWithProgress(body, type, token, onPct) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${MEDIA_BASE}/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', type);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onPct?.(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data);
      const err = new Error(data.error || `Upload failed (${xhr.status})`);
      Object.assign(err, data, { status: xhr.status, code: data.error });
      reject(err);
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(body);
  });
}

async function uploadToMedia(blob, type, onProgress) {
  const user = auth?.currentUser;
  if (!user || user.isAnonymous) throw Object.assign(new Error('Sign in to upload.'), { code: 'account_required' });
  onProgress?.({ phase: 'uploading', percent: 0 });
  const data = await putWithProgress(blob, type, await user.getIdToken(), (p) => onProgress?.({ phase: 'uploading', percent: p }));
  onProgress?.({ phase: 'done', percent: 100 });
  return { url: data.url, key: data.key, name: blob.name };
}

// Uploads an image and returns { url, name } with a URL ready to store on the
// deck. `quality` lets PDF page renders keep text crisp; `precompressed` skips
// compression for images the caller already encoded.
export async function uploadImage(file, { onProgress, quality, precompressed = false } = {}) {
  if (!file) throw new Error('No file provided.');
  if (!file.type?.startsWith('image/')) throw new Error('Only image files are supported.');
  if (!mediaEnabled) return uploadImageToDrive(file, { onProgress });
  const blob = precompressed ? file : await compressImage(file, { quality });
  return { ...(await uploadToMedia(blob, blob.type || file.type, onProgress)), name: file.name };
}

export async function uploadAudio(file, { onProgress } = {}) {
  if (!file) throw new Error('No file provided.');
  if (!mediaEnabled) return uploadAudioToDrive(file, { onProgress });
  const type = file.type || 'audio/webm';
  if (!type.startsWith('audio/')) throw new Error('Only audio files are supported.');
  return { ...(await uploadToMedia(file, type, onProgress)), name: file.name };
}

const mb = (bytes) => `${Math.round(bytes / (1024 * 1024))} MB`;

// What went wrong with an upload, in words a presenter can act on.
export function friendlyUploadError(err) {
  if (!mediaEnabled) return friendlyDriveError(err);
  switch (err?.code) {
    case 'over_quota':
      return `Your storage is full (${mb(err.usedBytes)} of ${mb(err.limitBytes)} used). Delete decks you no longer need, or upgrade to Pro for more space.`;
    case 'too_large':
      return `That file is too large (the limit is ${mb(err.maxBytes)}).`;
    case 'unsupported_type':
      return 'That file type isn’t supported. Use a JPEG, PNG, WebP, GIF or SVG image, or an MP3, M4A, WebM or OGG audio file.';
    case 'sign_in_required':
    case 'account_required':
      return 'Sign in with an account to upload files.';
  }
  const msg = String(err?.message || err || '');
  if (msg.includes('Network') || msg.includes('Failed to fetch')) return 'Network error during upload. Check your connection and try again.';
  return msg.length > 200 ? `${msg.slice(0, 200)}…` : msg;
}
